import {PageResponse, PaginationQueryParams} from "../interfaces/api/PaginationInterface";
import {
  Booking,
  BookingBillingRequest,
  BookingByStatusResponse,
  BookingDocumentFormData,
  BookingFileRequest,
  BookingItem,
  BookingManagementDto,
  BookingManagementTotalPriceDto,
  BookingPriceResponse,
  BookingSearchObject,
  BookingTableDto,
  BookingToDoDeleteRequest
} from "../interfaces/BookingInterfaces";
import {SearchFilter} from "../interfaces/api/FiltersInterface";
import {queryUtils} from "../utils/queryUtils";
import {httpService} from "./HttpService";
import {
  API_BOOKINGS_BY_STATUS_PATH,
  API_BOOKINGS_PATH,
  API_BOOKINGS_DOCUMENT_PATH,
  API_BOOKINGS_CANCEL_PATH, API_BOOKINGS_KEEP_PATH
} from "../constants/routes/RoutePaths";
import {PurchaseBookingRequest} from "../interfaces/PurchaseInterfaces";
import {fileUtils} from "../utils/fileUtils";
import {ContentTypeEnum} from "../constants/fileType";
import { DocumentDTO } from "interfaces/FileInterfaces";

function createBooking (booking: PurchaseBookingRequest): Promise<Booking> {
  return httpService.post<Booking>(`${API_BOOKINGS_PATH}`, booking)
}

function getBookingsPage(params: PaginationQueryParams, filterParams: BookingSearchObject): Promise<PageResponse<BookingTableDto>> {
  const queryParams = queryUtils.buildPageAndFilterQueryParams(params, filterParams)
  return httpService.get<PageResponse<BookingTableDto>>(`${API_BOOKINGS_PATH}${queryParams}`)
}

function getBookingsByStatus(filterParams: SearchFilter): Promise<BookingByStatusResponse> {
  const params = queryUtils.buildFilterParams(filterParams);
  const stringParams = params.length > 0 ? `?${params.join("&")}` : ""
  return httpService.get<BookingByStatusResponse>(`${API_BOOKINGS_BY_STATUS_PATH}${stringParams}`)
}

function getBookingDetails(id: string): Promise<BookingItem> {
  return httpService.get<BookingItem>(`${API_BOOKINGS_PATH}/${id}`);
}

function updateBooking (id: string, booking: PurchaseBookingRequest): Promise<Booking> {
  return httpService.put<Booking>(`${API_BOOKINGS_PATH}/${id}`, booking)
}

function deleteBooking (id: string): Promise<Response> {
  return httpService.delete(`${API_BOOKINGS_PATH}/${id}`)
}

function addFiles(id: string, file: FileList, request: BookingFileRequest): Promise<DocumentDTO> {
  const formData = fileUtils.createFormDataWithFiles(file);
  formData.set("request", new Blob([JSON.stringify(request)],{type: "application/json"}))
  return httpService.postFormData(`${API_BOOKINGS_PATH}/${id}/files`, formData);
}

function updatePendingBooking (id: string, booking: BookingDocumentFormData): Promise<BookingItem> {
  return httpService.put<BookingItem>(`${API_BOOKINGS_PATH}/${id}/pending`, booking);
}

function getDocumentById(fileId: string): Promise<Blob> {
  return httpService.getReadableStream(`${API_BOOKINGS_DOCUMENT_PATH}/${fileId}`);
}

function cancelBooking(id: string): Promise<BookingItem> {
  return httpService.put<BookingItem>(`${API_BOOKINGS_CANCEL_PATH}/${id}`, {});
}

function keepBooking(id: string): Promise<BookingItem> {
  return httpService.put<BookingItem>(`${API_BOOKINGS_KEEP_PATH}/${id}`, {});
}

function deleteDocumentById(fileId: string): Promise<Response> {
  return httpService.delete(`${API_BOOKINGS_DOCUMENT_PATH}/${fileId}`)
}

function deleteBookingsToDo (bookingToDoDeleteRequest: BookingToDoDeleteRequest): Promise<Response> {
  return httpService.delete(`${API_BOOKINGS_PATH}/to-do`, bookingToDoDeleteRequest)
}

function generateSepaFile(bookingIds: string[]): Promise<Blob> {
  return httpService.postReadableStream(`${API_BOOKINGS_PATH}/sepa`, {entityIds: bookingIds}, ContentTypeEnum.JSON)
}

function getBookingPriceForSupplier(bookingId: string, supplierId: string) : Promise<BookingPriceResponse> {
  return httpService.get<BookingPriceResponse>(`${API_BOOKINGS_PATH}/${bookingId}/price?supplierId=${supplierId}`)
}

function downloadBookingBills(params: BookingSearchObject): Promise<Blob> {
  const queryParams = queryUtils.buildPageAndFilterQueryParams(null, params)
  return httpService.getReadableStream(`${API_BOOKINGS_PATH}/bills${queryParams}`)
}

function updateBookingsMassCatalog(bookingIds: string[]): Promise<Response> {
  return httpService.put(`${API_BOOKINGS_PATH}/mass-catalog`, {bookingIds: bookingIds})
}

function getBookingsByGroupType(params: PaginationQueryParams, filterParams: BookingSearchObject): Promise<PageResponse<BookingManagementDto>> {
  const queryParams = queryUtils.buildPageAndFilterQueryParams(params, filterParams)
  return httpService.get<PageResponse<BookingManagementDto>>(`${API_BOOKINGS_PATH}/booking-management/${queryParams}`)
}

function getTotalPrice(params: BookingSearchObject): Promise<BookingManagementTotalPriceDto> {
const queryParams = queryUtils.buildPageAndFilterQueryParams(null, params);
return httpService.get<BookingManagementTotalPriceDto>(`${API_BOOKINGS_PATH}/booking-management/total-price${queryParams}`);
}

function getBookingManagementSummaryCsv(params: BookingSearchObject): Promise<Blob> {
  const queryParams = queryUtils.buildPageAndFilterQueryParams(null, params);
  return httpService.getReadableStream(`${API_BOOKINGS_PATH}/export/summary-csv${queryParams}`);
}

function createBookingBilling(request: BookingBillingRequest): Promise<BookingItem> {
  const formData = new FormData();
  formData.append("file", request.file);
  formData.append("number", request.number);
  return httpService.postFormData(`${API_BOOKINGS_PATH}/${request.bookingId}/billing`, formData);
}

function addBookingDocument(bookingId: string, file: File, invoiceDate?: string, invoiceNumber?: string): Promise<BookingItem> {
  const formData = new FormData();
  formData.append("file", file);
  formData.append("invoiceDate", invoiceDate);
  formData.append("invoiceNumber", invoiceNumber);
  return httpService.putFormData(`${API_BOOKINGS_PATH}/${bookingId}/document`, formData);
}

export const bookingService = {
  createBooking,
  getBookingsPage,
  updateBooking,
  deleteBooking,
  getBookingsByStatus,
  getBookingDetails,
  updatePendingBooking,
  getDocumentById,
  deleteDocumentById,
  cancelBooking,
  keepBooking,
  deleteBookingsToDo,
  generateSepaFile,
  getBookingPriceForSupplier,
  addFiles,
  downloadBookingBills,
  updateBookingsMassCatalog,
  getBookingsByGroupType,
  getTotalPrice,
  getBookingManagementSummaryCsv,
  createBookingBilling,
  addBookingDocument
}
